import { useCallback, useState } from 'react';

/**
 * React hook that ensures an async function can only run once at a time.
 *
 * Prevents double-click, rapid taps, or overlapping async calls.
 * When `isLocked` is true, subsequent calls to the wrapped function will be ignored
 * until the previous execution finishes.
 *
 * @example
 * ```tsx
 * const { runExclusive, isLocked } = useAsyncLock<boolean>();
 *
 * const toggleFavorite = runExclusive(async () => {
 *   if (isFavorite) {
 *     return await removeFavoriteGame(uid);
 *   } else {
 *     return await addFavoriteGame(uid);
 *   }
 * });
 *
 * <button onClick={toggleFavorite} disabled={isLocked}>
 *   {isLocked ? 'Saving...' : 'Favorite'}
 * </button>
 * ```
 *
 * @returns {Object} An object with:
 *  - `runExclusive`: function wrapper for your async function
 *  - `isLocked`: boolean indicating if the function is currently running
 */

export const useAsyncLock = <T>() => {
    const [isLocked, setIsLocked] = useState(false);

    const runExclusive = useCallback(
        (fn: (...args: any[]) => Promise<T> | T) => {
            return async (...args: Parameters<typeof fn>): Promise<T> => {
                if (isLocked) {
                    return Promise.resolve(undefined as unknown as T);
                }

                setIsLocked(true);
                try {
                    return await fn(...args);
                } finally {
                    setIsLocked(false);
                }
            };
        },
        [isLocked]
    );

    return { runExclusive, isLocked };
};
